import { fundingDraftOf } from '../../common/FundingFields';

// Ο κύκλος ζωής της επεξεργασίας πόρου, ως καθαρός reducer.
//
// Δεν είναι αφαίρεση για την αφαίρεση. Στην προηγούμενη έκδοση η ακύρωση ήταν
// `setEditMode()`, δηλαδή **μόνο** εναλλαγή της σημαίας `edit_mode`, ενώ τα πεδία
// ήταν μη ελεγχόμενα (`defaultValue`) και έγραφαν σε κατάσταση που η ακύρωση δεν
// άγγιζε. Το αποτέλεσμα:
//
//   1. Ο χρήστης πατά «Επεξεργασία» και αλλάζει τον τίτλο.
//   2. Πατά «Ακύρωση». Η οθόνη δείχνει τον **αρχικό** τίτλο, γιατί το `defaultValue`
//      διαβάζει από τον πόρο. Φαίνεται σωστό.
//   3. Πατά ξανά «Επεξεργασία» και μετά «Αποθήκευση», **χωρίς να αγγίξει τίποτα**.
//      Αποθηκεύεται ο τίτλος του βήματος 1, δηλαδή η αλλαγή που ακυρώθηκε.
//
// Καμία ένδειξη, κανένα σφάλμα, και η απώλεια δεν αναστρέφεται. Επειδή η μετάβαση
// είναι ακριβώς η ευκαιρία να ξαναγραφτεί αυτό, η λογική βγαίνει εδώ ώστε να
// **δοκιμάζεται** αντί να επιθεωρείται: βλ. `resourceEdit.test.js`.

/**
 * Οι τιμές του πόρου ως πρόχειρο επεξεργασίας.
 *
 * Ένα σημείο, ώστε η αρχικοποίηση και η επαναφορά μετά από ακύρωση να μην
 * μπορούν να αποκλίνουν: αν αποκλίνουν, επανεμφανίζεται το ίδιο σφάλμα.
 */
export function draftOf(resource) {
    return {
        title: resource.title ?? '',
        description: resource.description ?? '',
        image: resource.image ?? '',
        tags: resource.tags ?? [],
        funding: fundingDraftOf(resource.funding)
    };
}

/**
 * Τα πεδία **επιμέλειας** ενός πόρου: ό,τι έγραψε άνθρωπος και δεν είναι ταυτότητα.
 *
 * Ένας κατάλογος, ώστε κάθε σημείο που μεταφέρει επιμέλεια από πόρο σε φόρμα να τον
 * διαβάζει αντί να απαριθμεί πεδία με το χέρι. Η απαρίθμηση με το χέρι είναι που
 * έχασε την **εικόνα** στη διαδρομή διόρθωσης διεύθυνσης της D10β: μεταφέρονταν
 * τίτλος, περιγραφή και ετικέτες, και το `image` απλώς δεν είχε γραφτεί.
 *
 * Όποιο πεδίο προστεθεί εδώ, το κατοχυρώνει δοκιμή σε **κάθε** τέτοιο σημείο. Αφορά
 * άμεσα τα πεδία χρηματοδότησης του P2-3 και τα `relatedResources` του P3-2.
 */
export const CURATION_FIELDS = ['title', 'description', 'image', 'tags', 'funding'];

/**
 * Απόφαση D10, σκέλος β: το πρόχειρο της **φόρμας καταχώρησης**, γεμάτο με την
 * επιμέλεια ενός υπάρχοντος πόρου.
 *
 * Η **διεύθυνση μένει κενή**: αυτή ακριβώς διορθώνεται. Όλα τα υπόλοιπα μεταφέρονται
 * ώστε ο χρήστης να μη χάνει δουλειά επειδή διόρθωσε ένα πεδίο — η ΛΑ-10 ζητά να
 * μπορεί να διορθώνει, όχι να ξαναγράφει.
 *
 * Οι ετικέτες γίνονται συμβολοσειρά με κόμματα, γιατί αυτή είναι η μορφή του πεδίου
 * της φόρμας καταχώρησης — σε αντίθεση με τη φόρμα επεξεργασίας, που έχει `TagsField`.
 */
export function replacementDraftOf(resource) {
    return {
        url: '',
        title: resource.title ?? '',
        description: resource.description ?? '',
        image: resource.image ?? '',
        tags: (resource.tags ?? []).join(', '),
        funding: fundingDraftOf(resource.funding)
    };
}

/** Η αρχική κατάσταση: εκτός επεξεργασίας, με πρόχειρο ίσο με τον πόρο. */
export function initialEditState(resource) {
    return { isEditing: false, draft: draftOf(resource) };
}

/**
 * Οι μεταβάσεις. Κάθε μία που **βγάζει** τον χρήστη από την επεξεργασία
 * ξαναχτίζει το πρόχειρο από τον πόρο, χωρίς εξαίρεση.
 *
 * - `start`   — είσοδος στην επεξεργασία. Ξεκινά από τον πόρο, όχι από ό,τι έμεινε.
 * - `change`  — μεταβολή ενός πεδίου του προχείρου.
 * - `cancel`  — έξοδος **χωρίς** αποθήκευση. Το πρόχειρο επανέρχεται στον πόρο.
 * - `saved`   — έξοδος **μετά** από αποθήκευση. Ο γονέας ξαναφορτώνει και στέλνει
 *               νέο `resource`, οπότε το πρόχειρο χτίζεται από αυτό.
 * - `sync`    — ο πόρος άλλαξε από τον γονέα. Αγνοείται όσο ο χρήστης γράφει, ώστε
 *               μια ανανέωση στο παρασκήνιο να μη σβήνει ό,τι πληκτρολογεί.
 */
export function resourceEditReducer(state, action) {
    switch (action.type) {
        case 'start':
            return { isEditing: true, draft: draftOf(action.resource) };

        /**
         * **P1-5β: έναρξη με τις τιμές που ΑΠΟΡΡΙΦΘΗΚΑΝ στην προσάρτηση.**
         *
         * Το πρόχειρο χτίζεται από τον **πόρο** και μετά δέχεται από πάνω μόνο
         * όσα πεδία ο χρήστης πράγματι συμπλήρωσε. Δεν είναι λεπτομέρεια: με
         * σκέτη αντικατάσταση, ένα κενό πεδίο της φόρμας καταχώρησης θα **έσβηνε**
         * την τιμή του υπάρχοντος πόρου μόλις ο χρήστης πατούσε αποθήκευση —
         * δηλαδή η προώθηση θα κατέστρεφε επιμέλεια που κανείς δεν ζήτησε να
         * αλλάξει, από την πίσω πόρτα της D9.
         */
        case 'forward': {
            const base = draftOf(action.resource);
            const values = action.values ?? {};
            const draft = { ...base };

            for (const field of CURATION_FIELDS) {
                if (field === 'funding') continue;

                const value = values[field];
                if (value === undefined || value === null) continue;
                if (typeof value === 'string' && value.trim() === '') continue;
                if (Array.isArray(value) && value.length === 0) continue;
                draft[field] = value;
            }

            // ⚠ **Η χρηματοδότηση συγχωνεύεται ΠΕΔΙΟ ΠΡΟΣ ΠΕΔΙΟ, όχι ολόκληρη.**
            //
            // Το `fundingDraftOf` χτίζει **και τα έξι** κλειδιά από την πηγή του,
            // οπότε μια σκέτη ανάθεση θα αντικαθιστούσε ολόκληρο το υπο-έγγραφο:
            // πέντε πεδία που ο χρήστης δεν άγγιξε θα γίνονταν κενά, και η
            // αποθήκευση θα τα έσβηνε. Είναι η ίδια αστοχία με τα υπόλοιπα πεδία,
            // ένα επίπεδο βαθύτερα — και η πρώτη μορφή αυτού του κώδικα την είχε.
            const submittedFunding = fundingDraftOf(values.funding ?? {});
            for (const field of Object.keys(draft.funding)) {
                const value = submittedFunding[field];
                if (typeof value === 'string' && value.trim() === '') continue;
                if (value === undefined || value === null) continue;
                draft.funding = { ...draft.funding, [field]: value };
            }

            return { isEditing: true, draft };
        }

        case 'change':
            return { ...state, draft: { ...state.draft, [action.name]: action.value } };

        case 'cancel':
        case 'saved':
            return { isEditing: false, draft: draftOf(action.resource) };

        case 'sync':
            return state.isEditing ? state : { ...state, draft: draftOf(action.resource) };

        default:
            return state;
    }
}

// Το `hashPointsTo` **μετακόμισε** στο `services/browseState.js` ως
// `pointsToResource`, μαζί με τις δοκιμές του (P1-5).
//
// Δεν ήταν μετονομασία. Το `#<id>` δεν φτάνει ποτέ στον εξυπηρετητή, οπότε
// ταίριαζε μόνο με πόρο **ήδη φορτωμένο στη σελίδα**: με τη σελιδοποίηση και την
// αναζήτηση στον διακομιστή, σύνδεσμος προς πόρο της τρίτης σελίδας δεν άνοιγε
// τίποτα. Η αντικατάστασή του με παράμετρο ερωτήματος είναι η προϋπόθεση για να
// είναι ο μόνιμος σύνδεσμος πράγματι μόνιμος — και η παράμετρος ζει μαζί με την
// υπόλοιπη κατάσταση περιήγησης, αντί να είναι δεύτερος μηχανισμός.
